import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Tag,
  Text,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react'
import Document from '../../assets/svg/document'
import ImageIcon from '../../assets/svg/image'
import { ISharedDiagnostic } from '../../types/diagnostics'
import ContactHeader from './components/ContactHeader'
import { useNavigate } from 'react-router-dom'
import { useMyProfile } from '../../api/api_hooks/user/myProfile'
import ReceiverButtons from './components/ReceiverButtons'
import { useTranslation } from 'react-i18next'
import { LuEllipsisVertical, LuArchive } from 'react-icons/lu'
import { useArchiveSharedDiagnostic } from '../../api/api_hooks/shared-diagnostic'
import { useRef } from 'react'

export default function SharedCase({
  data,
  reloadReceivedSharedDiagnostics,
}: {
  data: ISharedDiagnostic
  reloadReceivedSharedDiagnostics?: () => void
}) {
  const navigation = useNavigate()
  const { data: currentUser } = useMyProfile()
  const isSender = currentUser?.id === data.patient?.doctor?.id
  const isReceiver = currentUser?.id === data.receiverDoctor?.id
  const isAccepted = data.receiverStatus === 'accepted'
  const hasAccesToWhiteboard = isSender || isAccepted
  const isExpired = data.status === 'expired'
  const isClosed = data.status === 'closed'
  const { t } = useTranslation()
  const toast = useToast()
  const { mutateAsync: archiveDiagnostic } = useArchiveSharedDiagnostic()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const openWhiteboard = () => {
    navigation(`/patients/${data.patientId}/diagnostics/${data.id}`, {
      replace: true,
    })
  }

  const handleArchiveClick = () => {
    onOpen()
  }

  const confirmArchive = async () => {
    try {
      await archiveDiagnostic(data.id)
      toast({
        title: t('sharedDiagnostic.archiveSuccess') || 'Demande archivée',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      onClose()
      if (reloadReceivedSharedDiagnostics) {
        reloadReceivedSharedDiagnostics()
      }
    } catch (error: any) {
      toast({
        title: t('shared.errors.errorMsg'),
        description:
          error.response?.data?.message ||
          t('sharedDiagnostic.archiveFailed') ||
          "Impossible d'archiver la demande.",
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
      onClose()
    }
  }

  return (
    <Card
      p={4}
      position="relative"
      mt={4}
      w={'100%'}
      opacity={isExpired || isClosed ? 0.6 : 1}
      filter={isExpired || isClosed ? 'grayscale(1)' : 'none'}
    >
      <CardHeader p={1}>
        <Flex align="flex-start" justify="space-between" gap={2}>
          <ContactHeader
            key={data.id}
            sender={data.patient?.doctor}
            receiver={data.receiverDoctor}
            receiverEmail={data.receiverEmail}
          />
          <Menu placement="bottom-end">
            <MenuButton
              as={IconButton}
              aria-label="Actions"
              icon={<LuEllipsisVertical />}
              variant="ghost"
              size="sm"
            />
            <MenuList>
              <MenuItem
                icon={<LuArchive />}
                onClick={handleArchiveClick}
                isDisabled={isClosed || isExpired}
              >
                {t('sharedDiagnostic.archive') || 'Archiver / stopper'}
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
        <Flex gap={1} pt={1} wrap={'wrap'}>
          {data?.tags?.map((tag: any) => (
            <Tag
              key={tag.id}
              bgColor={`${tag.color}30`}
              fontFamily={'Poppins'}
              letterSpacing={'0.6px'}
              fontSize={'small'}
            >
              #{tag.name}
            </Tag>
          ))}
        </Flex>
      </CardHeader>
      <CardBody p={0}>
        <Flex justifyContent={'space-between'} alignItems={'center'}>
          <Flex gap={2} alignItems={'center'}>
            <Document />
            <Text>{data?.title}</Text>
          </Flex>
          {hasAccesToWhiteboard && (
            <Button
              onClick={() => openWhiteboard()}
              leftIcon={<ImageIcon />}
              colorScheme="purple"
            >
              {t('sharedDiagnostic.openWhiteboard')}
            </Button>
          )}
          {isReceiver && (
            <ReceiverButtons
              diagnostic={data}
              reloadReceivedSharedDiagnostics={reloadReceivedSharedDiagnostics}
            />
          )}
        </Flex>
      </CardBody>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('sharedDiagnostic.archiveConfirmTitle') || 'Archiver cette demande ?'}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('sharedDiagnostic.archiveConfirmMessage') ||
                'Êtes-vous sûr de vouloir archiver cette demande ? Elle sera retirée de votre liste active.'}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                {t('shared.common.cancel') || 'Annuler'}
              </Button>
              <Button colorScheme="red" onClick={confirmArchive} ml={3}>
                {t('shared.common.confirm') || 'Confirmer'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Card>
  )
}
